import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { installStaleChunkReload } from "@seagames/ui";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import { OvrToaster } from "./components/ui/toast";
import { queryClient } from "./lib/api/query-client";
import { onSignOut } from "./lib/auth/token-store";
import { initI18n } from "./lib/i18n";
import { OvrUiStrings } from "./lib/i18n/ui-strings";
import { router } from "./router";

// A tab opened before a deploy asks for chunks that are gone: reload once to
// the new version instead of showing an error (before anything lazy loads).
installStaleChunkReload();

// Before the first render, so no frame paints in the wrong language.
await initI18n();

/**
 * The one bridge from the token store back to the UI: when the SERVER has
 * ruled on the credential (`isCredentialVerdict`), drop the cache and go to
 * /login, keeping the return path. A 503 or a network drop never reaches
 * here. An explicit sign-out (`reason: "user"`) navigates on its own.
 */
onSignOut((reason) => {
	queryClient.clear();
	if (reason === "user") return;
	const here = window.location.pathname + window.location.search;
	void router.navigate({
		to: "/login",
		search: { reason, redirect: here.startsWith("/login") ? undefined : here },
		replace: true,
	});
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("#root is missing from index.html");

createRoot(rootElement).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			{/* @seagames/ui reads its chrome strings (Filter, Rows per page…) from here, in the viewer's language. */}
			<OvrUiStrings>
				<RouterProvider router={router} />
			</OvrUiStrings>
			<OvrToaster />
		</QueryClientProvider>
	</StrictMode>,
);
